import {
  createContext,
  useContext,
  useReducer,
  type Dispatch,
  type ReactNode,
} from "react";
import {
  DIRECT_INDEX_MAX_POSITIONS,
  describeDirectIndex,
  type DirectIndexExclusion,
  type DirectIndexInclusion,
  type DirectIndexRebalancing,
  type DirectIndexUniverseRefLike,
  type DirectIndexWeighting,
} from "@repo/strategy/client";
import type { DirectIndexConstituent } from "../data/direct-index-queries";

export const DEFAULT_TOP_N = 5;
export const DEFAULT_REBALANCING: DirectIndexRebalancing = "daily";

export interface BuilderRecipeState {
  readonly universe: DirectIndexUniverseRefLike | null;
  readonly topN: number;
  readonly weighting: DirectIndexWeighting;
  readonly rebalancing: DirectIndexRebalancing;
  readonly excludes: readonly DirectIndexExclusion[];
  readonly includes: readonly DirectIndexInclusion[];
  /** Global stock additions that are not guaranteed to belong to the universe. */
  readonly addedAssets: readonly DirectIndexConstituent[];
  readonly name: string;
  readonly description: string;
  readonly descriptionIsCustom: boolean;
}

export interface BuilderRecipeRestore {
  readonly universe: DirectIndexUniverseRefLike;
  readonly topN: number;
  readonly weighting: DirectIndexWeighting;
  readonly rebalancing: DirectIndexRebalancing;
  readonly excludes: readonly DirectIndexExclusion[];
  readonly includes: readonly DirectIndexInclusion[];
}

export type BuilderRecipeAction =
  | { type: "reset" }
  | { type: "restore-draft"; recipe: BuilderRecipeRestore }
  | { type: "select-universe"; universe: DirectIndexUniverseRefLike }
  | { type: "set-top-n"; topN: number }
  | { type: "set-weighting"; weighting: DirectIndexWeighting }
  | { type: "set-rebalancing"; rebalancing: DirectIndexRebalancing }
  | { type: "exclude"; exclusion: DirectIndexExclusion }
  | { type: "include"; assetCanonicalId: string }
  | {
      type: "add-asset";
      inclusion: DirectIndexInclusion;
      asset?: DirectIndexConstituent;
      indexWeightBps?: number | null;
    }
  | { type: "set-name"; name: string }
  | { type: "set-description"; description: string }
  | { type: "reset-description" };

export const INITIAL_RECIPE_STATE: BuilderRecipeState = {
  universe: null,
  topN: DEFAULT_TOP_N,
  weighting: "equal",
  rebalancing: DEFAULT_REBALANCING,
  excludes: [],
  includes: [],
  addedAssets: [],
  name: "",
  description: "",
  descriptionIsCustom: false,
};

function autoDescription(state: BuilderRecipeState): string {
  if (!state.universe) {
    return "";
  }
  return describeDirectIndex({
    universe: state.universe,
    topN: state.topN,
    weighting: state.weighting,
    rebalancing: state.rebalancing,
    excludeTickers: state.excludes.map((exclusion) => exclusion.ticker),
  });
}

/** Regenerate the auto description unless the user has taken it over (FR7). */
function withDescription(state: BuilderRecipeState): BuilderRecipeState {
  if (state.descriptionIsCustom) {
    return state;
  }
  return { ...state, description: autoDescription(state) };
}

export function builderRecipeReducer(
  state: BuilderRecipeState,
  action: BuilderRecipeAction,
): BuilderRecipeState {
  switch (action.type) {
    case "reset":
      return INITIAL_RECIPE_STATE;
    case "restore-draft":
      return withDescription({
        ...INITIAL_RECIPE_STATE,
        ...action.recipe,
        excludes: [...action.recipe.excludes],
        includes: [...action.recipe.includes],
        name: action.recipe.universe.name,
        descriptionIsCustom: false,
      });
    case "select-universe": {
      const isSameUniverse =
        state.universe?.kind === action.universe.kind &&
        state.universe?.id === action.universe.id;
      return withDescription({
        ...state,
        universe: action.universe,
        // Changing universe resets excludes and keeps the user's rules. Index
        // weighting cannot carry into a sector, so fall back to equal there.
        excludes: isSameUniverse ? state.excludes : [],
        includes: isSameUniverse ? state.includes : [],
        addedAssets: isSameUniverse ? state.addedAssets : [],
        weighting:
          action.universe.kind === "sector" && state.weighting === "index"
            ? "equal"
            : state.weighting,
        name:
          state.name.length > 0 && isSameUniverse
            ? state.name
            : action.universe.name,
      });
    }
    case "set-top-n":
      return withDescription({ ...state, topN: action.topN });
    case "set-weighting":
      return withDescription({ ...state, weighting: action.weighting });
    case "set-rebalancing":
      return withDescription({ ...state, rebalancing: action.rebalancing });
    case "exclude": {
      const wasIncluded = state.includes.some(
        (item) => item.assetCanonicalId === action.exclusion.assetCanonicalId,
      );
      if (
        state.topN <= 1 ||
        (!wasIncluded &&
          state.excludes.some(
            (e) => e.assetCanonicalId === action.exclusion.assetCanonicalId,
          ))
      ) {
        return state;
      }
      return withDescription({
        ...state,
        excludes: wasIncluded
          ? state.excludes
          : [...state.excludes, action.exclusion],
        includes: state.includes.filter(
          (item) => item.assetCanonicalId !== action.exclusion.assetCanonicalId,
        ),
        addedAssets: state.addedAssets.filter(
          (item) => item.assetCanonicalId !== action.exclusion.assetCanonicalId,
        ),
        topN: Math.max(1, state.topN - 1),
      });
    }
    case "include": {
      const wasExcluded = state.excludes.some(
        (exclusion) => exclusion.assetCanonicalId === action.assetCanonicalId,
      );
      if (!wasExcluded) {
        return state;
      }
      return withDescription({
        ...state,
        excludes: state.excludes.filter(
          (e) => e.assetCanonicalId !== action.assetCanonicalId,
        ),
        topN: Math.min(DIRECT_INDEX_MAX_POSITIONS, state.topN + 1),
      });
    }
    case "add-asset": {
      if (
        state.includes.some(
          (item) => item.assetCanonicalId === action.inclusion.assetCanonicalId,
        )
      ) {
        return state;
      }
      const indexWeightBps =
        action.indexWeightBps ?? action.asset?.indexWeightBps ?? null;
      return withDescription({
        ...state,
        excludes: state.excludes.filter(
          (item) => item.assetCanonicalId !== action.inclusion.assetCanonicalId,
        ),
        includes: [...state.includes, action.inclusion],
        weighting:
          state.weighting === "index" && (indexWeightBps ?? 0) <= 0
            ? "equal"
            : state.weighting,
        addedAssets: action.asset
          ? [
              ...state.addedAssets.filter(
                (item) =>
                  item.assetCanonicalId !== action.asset?.assetCanonicalId,
              ),
              action.asset,
            ]
          : state.addedAssets,
        topN: Math.min(DIRECT_INDEX_MAX_POSITIONS, state.topN + 1),
      });
    }
    case "set-name":
      return { ...state, name: action.name };
    case "set-description":
      return {
        ...state,
        description: action.description,
        descriptionIsCustom: true,
      };
    case "reset-description":
      return withDescription({ ...state, descriptionIsCustom: false });
    default:
      return state;
  }
}

const BuilderRecipeStateContext = createContext<BuilderRecipeState | null>(
  null,
);
const BuilderRecipeDispatchContext =
  createContext<Dispatch<BuilderRecipeAction> | null>(null);

function BuilderRecipeRoot(props: {
  children: ReactNode;
  initialState?: BuilderRecipeState;
}) {
  const [state, dispatch] = useReducer(
    builderRecipeReducer,
    props.initialState ?? INITIAL_RECIPE_STATE,
  );
  return (
    <BuilderRecipeDispatchContext.Provider value={dispatch}>
      <BuilderRecipeStateContext.Provider value={state}>
        {props.children}
      </BuilderRecipeStateContext.Provider>
    </BuilderRecipeDispatchContext.Provider>
  );
}

/** Inherit the app-owned session across guest/auth provider remounts. */
export function BuilderRecipeProvider(props: {
  children: ReactNode;
  initialState?: BuilderRecipeState;
  inherit?: boolean;
}) {
  const inherited = useContext(BuilderRecipeStateContext);
  return props.inherit && inherited ? (
    props.children
  ) : (
    <BuilderRecipeRoot {...props} />
  );
}

export function useBuilderRecipeState(): BuilderRecipeState {
  const state = useContext(BuilderRecipeStateContext);
  if (!state) {
    throw new Error(
      "useBuilderRecipeState must be used within BuilderRecipeProvider",
    );
  }
  return state;
}

export function useBuilderRecipeDispatch(): Dispatch<BuilderRecipeAction> {
  const dispatch = useContext(BuilderRecipeDispatchContext);
  if (!dispatch) {
    throw new Error(
      "useBuilderRecipeDispatch must be used within BuilderRecipeProvider",
    );
  }
  return dispatch;
}
